/**
 * RegistroGastos.js — Lista de Registros de Gastos
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { ConfirmModal } from "../../../../shared/components/SharedUI";
import { TableToolbar, FilterField, FilterInput, FilterPills } from "../../shared/TableToolbar";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import AsientoDetalleModal, { MontoCelda } from "../../asientos/components/AsientoDetalleModal";

// ── helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ── Vista lista ───────────────────────────────────────────────────────────────
function ListaGastos({ onNuevo }) {
  const { t } = useTranslation();
  const [items,     setItems]     = useState([]);
  const [count,     setCount]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [montoMin,   setMontoMin]   = useState("");
  const [montoMax,   setMontoMax]   = useState("");
  const [loading,   setLoading]   = useState(true);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [detalleId, setDetalleId] = useState(null);
  const multimoneda = useMultimoneda();
  const [isUSD,     setIsUSD]     = useState(false);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeFilterCount = [fechaDesde, fechaHasta, montoMin, montoMax].filter(Boolean).length;

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE, tipo: "GASTO" });
    if (search.trim()) qs.set("search", search.trim());
    if (fechaDesde) qs.set("fecha_desde", fechaDesde);
    if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
    if (montoMin)   qs.set("monto_min", montoMin);
    if (montoMax)   qs.set("monto_max", montoMax);
    apiFetch(`/api/finance/asientos/?${qs}`)
      .then(r => r.json())
      .then(d => { setItems(d.results ?? []); setCount(d.count ?? 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search, fechaDesde, fechaHasta, montoMin, montoMax]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function showMsg(ok, text) {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 4000);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDelLoad(true);
    try {
      const res = await apiFetch(`/api/finance/asientos/${deleting.id}/`, { method: "DELETE" });
      if (!res.ok) throw new Error("Error al eliminar");
      setDeleting(null);
      showMsg(true, `Gasto ${deleting.numero} eliminado correctamente.`);
      fetchData();
    } catch (e) { showMsg(false, e.message); }
    finally { setDelLoad(false); }
  }

  const Btn = ({ onClick, bg, border, color, icon, title }) => (
    <button type="button" onClick={onClick} title={title}
      style={{ background: bg, border: `1px solid ${border}`,
        borderRadius: 6, padding: "4px 10px", color, cursor: "pointer" }}>
      <i className={`fa-solid ${icon}`}/>
    </button>
  );

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("registros.gastos")}</span>
      </span>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="title mt-2 mb-2">{t("registros.gastos")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("registros.administraGastos")}</p>
        </div>
        <button onClick={onNuevo} className="btn"
          style={{ padding: "9px 18px", background: "#f97316", borderRadius: 6,
            color: "#fff", fontWeight: 400, fontSize: 14,
            display: "flex", alignItems: "center", gap: 7, border: "none" }}>
          <i className="fa-solid fa-plus"/> {t("registros.nuevoGasto")}
        </button>
      </div>

      {/* Toast */}
      {msg && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: msg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${msg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${msg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center",
          gap: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380 }}>
          <i className={`fa-solid ${msg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: msg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }}/>
          <span style={{ color: msg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>{msg.text}</span>
        </div>
      )}

      {/* Tabla */}
      <div className={invoiceStyles["table-container"]}>
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("registros.buscarPlaceholderGastos")}
          loading={loading}
          activeFilterCount={activeFilterCount}
          onClearFilters={() => { setFechaDesde(""); setFechaHasta(""); setMontoMin(""); setMontoMax(""); setPage(1); }}
          extra={multimoneda && (
            <FilterPills
              value={isUSD ? "usd" : "crc"}
              onChange={(v) => setIsUSD(v === "usd")}
              options={[{ value: "crc", label: "₡ CRC" }, { value: "usd", label: "$ USD" }]}
            />
          )}
          filters={
            <>
              <FilterField label={t("common.fechaDesde")}>
                <FilterInput type="date" value={fechaDesde}
                  onChange={e => { setFechaDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.fechaHasta")}>
                <FilterInput type="date" value={fechaHasta}
                  onChange={e => { setFechaHasta(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMinimo")}>
                <FilterInput type="number" min="0" step="1000" placeholder="0" value={montoMin}
                  onChange={e => { setMontoMin(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMaximo")}>
                <FilterInput type="number" min="0" step="1000" placeholder={t("common.sinLimite")} value={montoMax}
                  onChange={e => { setMontoMax(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("asientos.numAsiento")}</th>
                <th>{t("registros.detalleReferencia")}</th>
                <th>{t("registros.bancoHaber")}</th>
                <th>{t("registros.cuentaGastoDebe")}</th>
                <th>{t("common.fecha")}</th>
                <th><div style={{ textAlign: "right" }}>{t("common.monto")}</div></th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }}/>
                  {t("registros.cargandoGastos")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }}/>
                  {t("registros.sinGastos")}
                </td></tr>
              ) : items.map(item => {
                return (
                  <tr key={item.id}>
                    <td style={{ fontFamily: "monospace", color: "#fb923c", fontWeight: 600 }}>
                      {item.numero}
                    </td>
                    <td>
                      <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{item.concepto}</span>
                      {item.referencia && (
                        <span style={{ display: "block", fontSize: "0.75rem",
                          color: "var(--text-secondary)", marginTop: 1 }}>{item.referencia}</span>
                      )}
                    </td>
                    <td style={{ color: "var(--text-primary)", fontSize: 13 }}>
                      {item.cuenta_debe_nombre ?? "—"}
                    </td>
                    <td style={{ color: "var(--text-primary)", fontSize: 13 }}>
                      {item.cuenta_haber_nombre ?? "—"}
                    </td>
                    <td>{fmtDate(item.fecha)}</td>
                    <td>
                      <MontoCelda crc={item.total_debitos} usd={item.total_debitos_usd}
                        isUSD={isUSD} color="#fb923c" />
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
                        <Btn onClick={() => setDetalleId(item.id)}
                          bg="rgba(96,165,250,0.1)"  border="rgba(96,165,250,0.3)"
                          color="#60a5fa" icon="fa-eye" title={t("common.ver")}/>
                        <Btn onClick={() => setDeleting(item)}
                          bg="rgba(239,68,68,0.1)" border="rgba(239,68,68,0.25)"
                          color="#f87171" icon="fa-trash" title={t("common.eliminar")}/>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }}/>
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("registros.registros")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {/* Modal detalle / edición — mismo componente que Asientos de Diario:
          incluye columnas en dólares y permite editar (propaga a asiento,
          movimiento bancario y saldo de la cuenta). */}
      {detalleId && (
        <AsientoDetalleModal
          asientoId={detalleId}
          autoConvertUsd
          onClose={() => setDetalleId(null)}
          onDeleted={() => { setDetalleId(null); fetchData(); }}
          onSaved={() => fetchData()}
        />
      )}

      <ConfirmModal
        open={!!deleting}
        name={deleting?.numero}
        message="Se eliminará el registro de gasto y su asiento de diario. Esta acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        loading={delLoad}
      />
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function RegistroGastos() {
  const navigate = useNavigate();
  return <ListaGastos onNuevo={() => navigate("/finanzas/gastos/nuevo")} />;
}
